import React from 'react';
import { getImageMedia, accent } from '../helpers';

/**
 * Template "standard" — un titre, un sous-titre, des points qui s'écrivent.
 *
 * Champs ajoutés tardivement, et pour une raison précise :
 *  - kicker : le petit label au-dessus du titre. Il n'existait que sur
 *      `statement`, et le repère « Cas nº 2 » avait dû se réfugier dans le
 *      SOUS-TITRE de la slide d'ouverture de ce cas — donc mêlé au propos,
 *      donc invisible. Les trois cas s'ouvrent maintenant de la même façon.
 *  - accent : "cyan" (défaut) | "red" | "amber" | "violet". Colore le titre et
 *      le kicker. Voir ACCENTS dans helpers.js.
 *
 * ⚠️ LA HAUTEUR EST LA CONTRAINTE, PAS LA LARGEUR. Les cartes étaient posées
 * à `min-h-[80px]` + `p-5` en pixels FIXES pendant que la page de conférence
 * met le `rem` racine à l'échelle de la LARGEUR (×0,85 en 1920). Résultat :
 * sur « Où est le goulot ? » — cinq points, dont trois passent à la ligne —
 * le cinquième sortait par le bas de l'écran, et `justify-center` le rendait
 * invisible sans rien signaler. Au-delà de trois points la slide passe donc en
 * mode DENSE : les respirations sont exprimées en `vh`, la hauteur plancher
 * des cartes disparaît, et le titre est borné par la hauteur autant que par sa
 * taille nominale. En dessous de quatre points, rien ne change — les slides à
 * deux ou trois points gardent leur air.
 */
const StandardSlide = ({ slide, slideKey, currentSlide, slidesLength, currentPointIndex, typingStates }) => {
    const ton = accent(slide.accent);
    const dense = (slide.points?.length || 0) > 3;

    return (
        <div key={slideKey} className="h-full p-12 flex flex-col justify-center relative overflow-hidden">
            <div className="absolute inset-0 opacity-5">
                <div className="w-full h-full" style={{
                    backgroundImage: 'linear-gradient(rgba(59, 130, 246, 0.3) 1px, transparent 1px), linear-gradient(90deg, rgba(59, 130, 246, 0.3) 1px, transparent 1px)',
                    backgroundSize: '50px 50px',
                    animation: 'fadeInScale 0.8s ease-out'
                }} />
            </div>

            <div className="relative z-10">
                {slide.kicker && (
                    <p className={`font-mono uppercase tracking-[0.3em] text-xl opacity-70 mb-3 animate-fade-in-up ${ton.texte}`}>
                        {slide.kicker}
                    </p>
                )}
                <h2
                    className={`pb-2 font-bold mb-4 bg-gradient-to-r ${ton.titre} bg-clip-text text-transparent animate-fade-in-up`}
                    style={{ fontSize: dense ? 'clamp(2rem, min(3.75rem, 7.5vh), 3.75rem)' : undefined }}
                >
                    {slide.title}
                </h2>
                <p className={`text-2xl text-blue-200/60 animate-fade-in-up stagger-1 ${dense ? 'mb-[3vh] leading-snug' : 'mb-8'}`}>
                    {slide.subtitle}
                </p>

                <div className={`max-w-3xl mx-auto ${dense ? 'space-y-[1.5vh]' : 'space-y-4'}`}>
                    {slide.points.map((point, i) => (
                        <div key={i} className={`flex items-start gap-4 bg-blue-500/5 rounded-xl border border-blue-500/10 hover:border-blue-500/30 hover:bg-blue-500/10 transition-all ${dense ? 'px-5 py-[1.6vh]' : 'p-5 min-h-[80px]'} ${i < currentPointIndex ? 'opacity-100' :
                            i === currentPointIndex ? 'opacity-100 animate-slide-in' :
                                'opacity-0'
                            }`}>
                            <div className={`w-4 h-4 mt-1.5 rounded-full flex-shrink-0 ${i <= currentPointIndex ? 'bg-cyan-400 animate-pulse-glow' : 'bg-blue-500/30'
                                }`} />
                            <p className={`text-2xl text-slate-300 m-0 font-mono flex-1 ${dense ? 'leading-snug' : 'leading-relaxed'}`}>
                                {i < currentPointIndex ? point :
                                    i === currentPointIndex ? (
                                        <>
                                            {typingStates[i] || ''}
                                            {typingStates[i] !== point && <span className="typing-cursor"></span>}
                                        </>
                                    ) : ''}
                            </p>
                        </div>
                    ))}
                </div>

                {slide.image && (
                    <div className="absolute bottom-12 right-12 w-48 h-48 rounded-xl border border-blue-500/20 bg-gradient-to-br from-slate-800 to-slate-900 flex items-center justify-center overflow-hidden animate-fade-in-scale stagger-4">
                        <div className="absolute inset-0 animate-shimmer" />
                        <div className="relative z-10 w-full h-full flex items-center justify-center scale-50">
                            {getImageMedia(slide.image)}
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default StandardSlide;
